import React, {useContext, useEffect, useRef, useState} from 'react';
import {AccessibilityInfo, View} from 'react-native';
import {isEmpty, noop} from 'lodash';

import KeyboardFocusable from '/component/base/ExtKeyboard/KeyboardFocusable';
import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import {Reports} from '/constant/TestIDs';
import {withTranslation} from '/provider/TranslationProvider';
import {getTextFromReactNode} from '/util/accessibility.util';
import {useAccessibilityFocus} from '/util/hooks/useAccessibilityFocus.hook';
import testProperties from '/util/testing/testProperties';

import {styles} from './ReportHeader.style';

import {RecordsModalContext} from '/context/RecordsModal';
interface InfoData {
  header?: string;
  body?: string;
}
interface ReportHeaderProps {
  title: React.ReactNode;
  description: React.ReactNode;
  infoData: InfoData;
  filtersCount: number;
  onFilterButtonPress: () => void;
  onInfoButtonClick: (data: {
    heading?: string;
    body?: string;
    triggerRef?: React.RefObject<any>;
    isScreenReaderOn?: boolean;
  }) => void;
  isTopLineTitle: boolean;
  isCSBReport: boolean;
}

export const ReportHeader: React.FC<ReportHeaderProps> = ({
  title = '',
  description = '',
  infoData = {},
  onInfoButtonClick = noop,
  isTopLineTitle = false,
  isCSBReport = false,
}) => {
  const titleText = getTextFromReactNode(title);
  const infoTitleText = titleText || infoData?.header || '';
  const infoButtonRef = useRef(null);
  const {isModalOpen} = useContext(RecordsModalContext);
  const isScreenReaderEnabled = useAccessibilityFocus(infoButtonRef);
  const infoAccessibilityLabel = infoTitleText
    ? `${infoTitleText} info icon, button`
    : 'Report information icon, button';
  const isInfoFocusSuppressed = Boolean(isModalOpen);
  const infoFocusProps = isInfoFocusSuppressed
    ? {disableFocusRing: true, focusable: false}
    : {};

  const [isScreenReaderOn, setIsScreenReaderOn] = useState(false);
  useEffect(() => {
    AccessibilityInfo.isScreenReaderEnabled().then(setIsScreenReaderOn);

    const sub = AccessibilityInfo.addEventListener(
      'screenReaderChanged',
      setIsScreenReaderOn,
    );

    return () => sub.remove();
  }, []);

  return (
    <View style={styles.base} accessible={false}>
      <View style={styles.flexDirection} accessible={false}>
        {!isTopLineTitle && (
          <View style={{flexShrink: 1}} accessible={false}>
            <Text
              testID={Reports.reportTitle}
              variant="large"
              allowFontScaling
              maxFontSizeMultiplier={2}
              accessible={true}
              importantForAccessibility="yes"
              accessibilityRole="header"
              accessibilityLabel={typeof title === 'string' ? title : undefined}
              style={
                !isEmpty(infoData) ? styles.infoDataheading : styles.heading
              }>
              {title}
            </Text>
          </View>
        )}
        {!isEmpty(infoData) ? (
          <View style={{flexShrink: 1}} accessible={!isScreenReaderEnabled}>
            <KeyboardFocusable
              viewRef={infoButtonRef}
              {...testProperties(null)}
              accessible={true}
              accessibilityLabel={infoAccessibilityLabel}
              accessibilityHint={
                titleText
                  ? `Opens details about ${titleText}`
                  : 'Opens details about this report'
              }
              accessibilityRole="button"
              importantForAccessibility="yes"
              {...infoFocusProps}
              onPress={() =>
                onInfoButtonClick({
                  heading: infoData.header,
                  body: infoData.body,
                  triggerRef: infoButtonRef,
                  isScreenReaderOn: isScreenReaderOn,
                })
              }>
              <Icon name="info" style={styles.infoIcon} accessible={false} />
            </KeyboardFocusable>
          </View>
        ) : null}
      </View>

      {description && (
        <Text
          allowFontScaling
          maxFontSizeMultiplier={2}
          accessibilityLabel={
            typeof description === 'string' ? description : undefined
          }
          accessible={true}
          testID={Reports.reportDescription}
          variant="small"
          emphasis="standard"
          style={
            !isEmpty(infoData)
              ? {}
              : isCSBReport
              ? styles.CSBsubheading
              : styles.subheading
          }>
          {description}
        </Text>
      )}
      <View style={styles.buttons}>
        {/* {isFunction(onFilterButtonPress) && (
  <ReportIconButton
    iconName="filter"
    badgeCount={filtersCount}
    onPress={onFilterButtonPress}>
    {tt('filter.label', 'Filters')}
  </ReportIconButton>
)} */}
      </View>
    </View>
  );
};

export default withTranslation('screen.report')(ReportHeader);
